import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { createWatchedPick } from '../../services/api';
import { formatPickTypeLabel, formatPickValue } from '../../utils/formatPickType';

/** Game statuses that mean the game has not yet started (tracking allowed). */
const NOT_STARTED_STATUSES = ['Scheduled', 'Pre-Game', 'Preview', 'Warmup'];

/** Parse American odds string (e.g. "+150", "-200") to number, or null if invalid. */
function parseAmericanOdds(str) {
  if (str == null || typeof str !== 'string') return null;
  const s = str.trim().replace(/^\+/, '');
  if (s === '') return null;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return Math.round(n);
}

/** American to decimal. */
function americanToDecimal(american) {
  if (american == null || Number.isNaN(american)) return null;
  return american > 0 ? american / 100 + 1 : 100 / Math.abs(american) + 1;
}

/** Decimal to American (rounded). */
function decimalToAmerican(decimal) {
  if (decimal == null || Number.isNaN(decimal) || decimal <= 1) return null;
  return decimal >= 2 ? Math.round((decimal - 1) * 100) : Math.round(-100 / (decimal - 1));
}

/** Format American odds for display. */
function formatAmericanDisplay(american) {
  if (american == null || Number.isNaN(american)) return '';
  const sign = american > 0 ? '+' : '';
  return `${sign}${american}`;
}

export function isGameNotStarted(gameStatus) {
  if (!gameStatus || typeof gameStatus !== 'string') return false;
  return NOT_STARTED_STATUSES.includes(gameStatus.trim());
}

const TrackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: ${(props) => (props.$compact ? '0.35rem' : '0.25rem 0.5rem')};
  min-width: ${(props) => (props.$compact ? '2rem' : 'auto')};
  font-size: 0.8rem;
  background: ${props => props.theme.primary};
  color: ${props => props.theme.primaryContrast || '#fff'};
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.9; }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${props => props.theme.border || props.theme.secondaryBg};
    color: ${props => props.theme.secondaryText};
  }
`;

/** Compact primary button for the login CTA next to gated model insights copy. */
const LoginCtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.65rem;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${(props) => props.theme.primary};
  color: ${(props) => props.theme.primaryContrast || '#fff'};
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
`;

/**
 * Shown when SHAP / model insights require auth: button navigates to login.
 *
 * @param {{ theme: object, variant?: 'card' | 'table' }} props
 */
export function LoginForInsightsPrompt({ theme, variant = 'table' }) {
  const navigate = useNavigate();
  const isCard = variant === 'card';
  const baseFlex = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.35rem',
  };
  const wrapperStyle = isCard
    ? {
        margin: '0.5rem 0 0',
        marginBottom: 0,
        padding: '0.35rem 0.5rem',
        fontSize: '0.85rem',
        color: theme.secondaryText,
        background: theme.tertiaryBg,
        borderRadius: '0.35rem',
        border: `1px solid ${theme.border}`,
        textAlign: 'center',
        justifyContent: 'center',
        ...baseFlex,
      }
    : {
        margin: '0.25rem 0 0',
        marginBottom: 0,
        fontSize: '0.85rem',
        color: theme.secondaryText,
        ...baseFlex,
      };

  return (
    <p style={wrapperStyle}>
      <LoginCtaButton type="button" theme={theme} onClick={() => navigate('/login')}>
        Log in
      </LoginCtaButton>
      <span> to track pick and view model insights</span>
    </p>
  );
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
`;

const ModalBox = styled.div`
  background: ${props => props.theme.cardBg || props.theme.primaryBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.5rem;
  padding: 1.25rem;
  max-width: 400px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.75rem;
  color: ${props => props.theme.primaryText};
  font-size: 1.1rem;
`;

const ModalSummary = styled.p`
  margin: 0 0 1rem;
  color: ${props => props.theme.secondaryText};
  font-size: 0.9rem;
`;

const ModalInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
  color: ${props => props.theme.secondaryText};
  font-size: 0.875rem;
`;

const OptionalIcon = styled(FaInfoCircle).attrs({ title: 'Optional' })`
  opacity: 0.7;
  flex-shrink: 0;
`;

const ModalInputWrap = styled.div`
  display: flex;
  align-items: stretch;
  margin-bottom: 1rem;
  border-radius: 0.35rem;
  overflow: hidden;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.inputBg};
`;

const ModalInputPrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  background: ${props => props.theme.inputBg};
  color: ${props => props.theme.secondaryText};
  font-size: 1rem;
  border-right: 1px solid ${props => props.theme.border};
`;

const ModalInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
  font-size: 1rem;
  box-sizing: border-box;
  &:focus { outline: none; }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const ModalButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.35rem;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.$primary ? props.theme.primary : 'transparent'};
  color: ${props => props.$primary ? (props.theme.primaryContrast || '#fff') : props.theme.primaryText};
  &:hover { opacity: 0.9; }
`;

const OddsFormatToggle = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
`;
const OddsFormatBtn = styled.button`
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 0.25rem;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.$active ? props.theme.primary : 'transparent'};
  color: ${props => props.$active ? (props.theme.primaryContrast || '#fff') : props.theme.primaryText};
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

/**
 * Renders a "Track" button for a prediction row when the user is logged in and the game has not started.
 * Opens a modal to optionally add a simulated wager and calls createWatchedPick.
 *
 * @param {Object} props
 * @param {Object} props.pick - Prediction row: playerId, playerName, gameId, gameStatus, teamId, teamName
 * @param {string} props.gameDate - YYYY-MM-DD
 * @param {string} props.pickType - 'total_bases' | 'hit' | 'two_hit' | 'hr'
 * @param {number} [props.predictionValue] - Model value (probability or TB)
 * @param {string} [props.oddsDisplay] - e.g. "+150 (draftkings)" for summary
 * @param {string} [props.defaultOddsDisplay] - raw American odds to prefill (e.g. "+150" or "-200")
 * @param {string|number} [props.edgeDisplay] - e.g. "12.5%" for modal
 */
function TrackPickButton({ pick, gameDate, pickType, predictionValue, oddsDisplay, defaultOddsDisplay, edgeDisplay, compact = false }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [stake, setStake] = useState('');
  const [oddsInput, setOddsInput] = useState('');
  const [oddsFormat, setOddsFormat] = useState('american');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // When modal opens, prefill odds from table (best or selected sportsbook)
  useEffect(() => {
    if (open) {
      const raw = (defaultOddsDisplay != null && defaultOddsDisplay !== '') ? String(defaultOddsDisplay).trim() : '';
      setOddsInput(raw);
      setOddsFormat('american');
    }
  }, [open, defaultOddsDisplay]);

  if (!pick || !isGameNotStarted(pick.gameStatus)) return null;

  const handleOpen = (e) => {
    if (e) e.stopPropagation();
    if (!user) return;
    setOpen(true);
    setError(null);
    setStake('');
  };

  const setOddsFormatAmerican = () => {
    if (oddsFormat === 'decimal') {
      const dec = parseFloat(oddsInput);
      if (!Number.isNaN(dec) && dec > 1) {
        const american = decimalToAmerican(dec);
        setOddsInput(american != null ? formatAmericanDisplay(american) : oddsInput);
      }
    }
    setOddsFormat('american');
  };
  const setOddsFormatDecimal = () => {
    if (oddsFormat === 'american') {
      const american = parseAmericanOdds(oddsInput);
      if (american != null) {
        const dec = americanToDecimal(american);
        setOddsInput(dec != null ? String(dec) : oddsInput);
      }
    }
    setOddsFormat('decimal');
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setOpen(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    if (e) e.stopPropagation();
    if (!gameDate || !pick.gameId || !pick.playerId || !pick.playerName) return;
    setLoading(true);
    setError(null);
    try {
      const stakeDollars = stake.trim() === '' ? undefined : parseFloat(stake);
      if (stakeDollars !== undefined && (isNaN(stakeDollars) || stakeDollars < 0)) {
        setError('Enter a valid dollar amount or leave empty.');
        setLoading(false);
        return;
      }
      let oddsAmerican = null;
      let oddsDisplayStr = null;
      const oddsStr = oddsInput.trim();
      if (oddsStr !== '') {
        if (oddsFormat === 'american') {
          oddsAmerican = parseAmericanOdds(oddsStr);
          if (oddsAmerican == null) {
            setError('Enter valid American odds (e.g. +150 or -200).');
            setLoading(false);
            return;
          }
          oddsDisplayStr = formatAmericanDisplay(oddsAmerican);
        } else {
          const dec = parseFloat(oddsStr);
          if (Number.isNaN(dec) || dec <= 1) {
            setError('Enter valid decimal odds (e.g. 1.92, must be > 1).');
            setLoading(false);
            return;
          }
          oddsAmerican = decimalToAmerican(dec);
          oddsDisplayStr = formatAmericanDisplay(oddsAmerican);
        }
      }
      const body = {
        game_date: gameDate,
        game_id: String(pick.gameId),
        player_id: pick.playerId,
        player_name: pick.playerName,
        pick_type: pickType,
        prediction_value: predictionValue,
        team_id: pick.teamId ?? undefined,
        team_name: pick.teamName ?? undefined,
        stake_dollars: stakeDollars,
      };
      if (oddsAmerican != null) body.odds_american = oddsAmerican;
      if (oddsDisplayStr != null) body.odds_display = oddsDisplayStr;
      await createWatchedPick(body);
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add pick. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const summaryText = [
    pick.playerName,
    formatPickTypeLabel(pickType),
    predictionValue != null && `(${formatPickValue(pickType, predictionValue)})`,
    oddsDisplay && `· Odds ${oddsDisplay}`,
    edgeDisplay != null && edgeDisplay !== '' && `· ${edgeDisplay}`,
  ].filter(Boolean).join(' ');

  return (
    <>
      <TrackButton type="button" theme={theme} $compact={compact} disabled={!user} onClick={handleOpen} title={user ? 'Track this pick' : 'Log in to track'}>
        <FaEye /> {!compact && 'Track'}
      </TrackButton>
      {open && (
        <ModalOverlay onClick={handleClose} role="dialog" aria-labelledby="track-pick-title">
          <ModalBox theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalTitle id="track-pick-title" theme={theme}>Track this pick</ModalTitle>
            <ModalSummary theme={theme}>{summaryText}</ModalSummary>
            <ModalInputLabel theme={theme}>
              Simulated wager <OptionalIcon theme={theme} aria-label="Optional" />
            </ModalInputLabel>
            <ModalInputWrap theme={theme}>
              <ModalInputPrefix theme={theme}>$</ModalInputPrefix>
              <ModalInput
                theme={theme}
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 10"
                value={stake}
                onChange={(e) => setStake(e.target.value)}
                disabled={loading}
              />
            </ModalInputWrap>
            <ModalInputLabel theme={theme}>
              Odds <OptionalIcon theme={theme} aria-label="Optional" />
            </ModalInputLabel>
            <OddsFormatToggle>
              <OddsFormatBtn theme={theme} $active={oddsFormat === 'american'} onClick={setOddsFormatAmerican} type="button">
                American
              </OddsFormatBtn>
              <OddsFormatBtn theme={theme} $active={oddsFormat === 'decimal'} onClick={setOddsFormatDecimal} type="button">
                Decimal
              </OddsFormatBtn>
            </OddsFormatToggle>
            <ModalInput
              theme={theme}
              type="text"
              inputMode={oddsFormat === 'decimal' ? 'decimal' : 'text'}
              placeholder={oddsFormat === 'american' ? 'e.g. +150 or -200' : 'e.g. 1.92'}
              value={oddsInput}
              onChange={(e) => setOddsInput(e.target.value)}
              disabled={loading}
              style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', width: '100%', boxSizing: 'border-box', border: `1px solid ${theme.border}`, borderRadius: '0.35rem', background: theme.inputBg, color: theme.primaryText, fontSize: '1rem' }}
            />
            {error && <p style={{ color: theme.error || '#e53e3e', fontSize: '0.875rem', margin: '0 0 0.5rem' }}>{error}</p>}
            <ModalActions>
              <ModalButton theme={theme} onClick={handleClose} disabled={loading}>Cancel</ModalButton>
              <ModalButton theme={theme} $primary onClick={handleSubmit} disabled={loading}>
                {loading ? 'Adding…' : 'Add to my picks'}
              </ModalButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
}

export default TrackPickButton;
